import { TableCell } from '@material-ui/core';

import { useRouteRef } from '@backstage/core-plugin-api';

import { LabelChips, useLabelFilters } from '@spotify/confidence-common-react';
import {
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
  formatDatePast,
  useThrottledPrefetch,
} from '@spotify/confidence-core-react';
import {
  RolloutListItemFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';
import { usePlanningSubtitle } from '@spotify/plugin-confidence-workflows-module-planning';
import {
  WorkflowInstanceTableRow,
  workflowInstanceRouteRef,
} from '@spotify/plugin-confidence-workflows-react';
import {
  NameCell,
  OwnerCell,
  StateCell,
  UpdateCell,
  useStateCell,
} from '@spotify/plugin-confidence-workflows-react';

import { useRolloutPrefetch } from '../../hooks';
import { tabForState } from '../RolloutWorkflowInstancePage/utils';
import { ProgressCell } from './ProgressCell';

export function RolloutTableRow({
  workflowInstance,
}: {
  workflowInstance: RolloutListItemFragment;
}) {
  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const href = workflowInstanceRoute({
    workflow: extractWorkflowFromInstanceName(workflowInstance.name) ?? '',
    id: extractLastNameComponent(workflowInstance.name) ?? '',
  });

  // Prefetching the workflow instance when hovering over the row, so that
  // when the user clicks on the row, the workflow instance is already loaded.
  const prefetch = useRolloutPrefetch();
  const { onMouseEnter, onMouseLeave } = useThrottledPrefetch(() =>
    prefetch(workflowInstance.name),
  );

  const tab = tabForState(workflowInstance);
  const stateProps = useStateCell(workflowInstance);
  const { addLabelFilter, labelDefinitions } = useLabelFilters();
  const hasLabels = labelDefinitions.length > 0;

  const planningText = usePlanningSubtitle(
    workflowInstance.moduleData2.planning,
  );

  const secondaryText =
    workflowInstance.state === 'draft'
      ? `Created ${formatDatePast(new Date(workflowInstance.createTime))}`
      : planningText;

  return (
    <WorkflowInstanceTableRow
      workflowInstance={workflowInstance}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      transform={path => `${path}/${tab}`}
    >
      <NameCell
        kind="Rollout"
        displayName={workflowInstance.displayName}
        comments={getTypeOrNull(workflowInstance.comments)}
        secondaryText={secondaryText}
        href={href}
        labels={workflowInstance.labels}
      />
      <StateCell {...stateProps} />
      <ProgressCell workflowInstance={workflowInstance} />
      <UpdateCell updateTime={workflowInstance.updateTime} />
      <OwnerCell owner={getTypeOrNull(workflowInstance.owner)} />
      {hasLabels && (
        <TableCell>
          <LabelChips
            labels={workflowInstance.labels}
            labelDefinitions={labelDefinitions}
            onLabelClick={addLabelFilter}
          />
        </TableCell>
      )}
    </WorkflowInstanceTableRow>
  );
}
